import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    getFacilityProducts,
    createFacilityProduct,
    updateFacilityProduct,
    deleteFacilityProduct,
} from './api';
import { facilityProductKeys } from './keys';
import { CreateFacilityProductBody, UpdateFacilityProductBody } from './types';

export const useFacilityProductList = ({
    centerId,
    enabled = true,
}: {
    centerId: string;
    enabled?: boolean;
}) =>
    useQuery({
        queryKey: facilityProductKeys.list(centerId),
        queryFn: () => getFacilityProducts({ centerId }),
        enabled: enabled && !!centerId,
        select: (data) => {
            if (!data?.data) return data?.data;
            const raw = data.data as any;
            // API 응답: { products[] } → 프론트: { results[] }
            return {
                ...raw,
                results: raw.results ?? raw.products ?? [],
            };
        },
    });

export const useCreateFacilityProduct = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ centerId, body }: { centerId: string; body: CreateFacilityProductBody }) =>
            createFacilityProduct({ centerId, body }),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: facilityProductKeys.all });
        },
    });
};

export const useUpdateFacilityProduct = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({
            centerId,
            productId,
            body,
        }: {
            centerId: string;
            productId: string;
            body: UpdateFacilityProductBody;
        }) => updateFacilityProduct({ centerId, productId, body }),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: facilityProductKeys.all });
        },
    });
};

export const useDeleteFacilityProduct = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ centerId, productId }: { centerId: string; productId: string }) =>
            deleteFacilityProduct({ centerId, productId }),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: facilityProductKeys.all });
        },
    });
};
